// Browser debugging information utility
// Collects useful browser data for debugging issues
class BrowserDebugCollector {
    async collect() {
        // Check if we're in a browser environment
        if (typeof window === 'undefined') {
            return this.getEmptyData();
        }
        return {
            performance: this.getPerformanceMetrics(),
            memory: this.getMemoryInfo(),
            network: this.getNetworkInfo(),
            storage: this.getStorageInfo(),
            mediaDevices: await this.getMediaDevices(),
            capabilities: this.getBrowserCapabilities(),
            pageInfo: this.getPageInfo(),
            resources: this.getResourceInfo(),
            viewportSize: this.getViewportSize(),
            scroll: this.getScrollInfo(),
            timestamp: new Date().toISOString()
        };
    }
    getPerformanceMetrics() {
        if (!window.performance || !window.performance.timing) {
            return {};
        }
        const timing = window.performance.timing;
        const navigation = timing.navigationStart;
        const metrics = {
            pageLoadTime: timing.loadEventEnd - navigation,
            domContentLoadedTime: timing.domContentLoadedEventEnd - navigation,
            timeToFirstByte: timing.responseStart - navigation,
            domInteractive: timing.domInteractive - navigation,
            domComplete: timing.domComplete - navigation
        };
        // Get paint metrics if available
        if (window.performance.getEntriesByType) {
            const paintEntries = window.performance.getEntriesByType('paint');
            paintEntries.forEach((entry) => {
                if (entry.name === 'first-paint') {
                    metrics.firstPaint = entry.startTime;
                }
                else if (entry.name === 'first-contentful-paint') {
                    metrics.firstContentfulPaint = entry.startTime;
                }
            });
        }
        return metrics;
    }
    getMemoryInfo() {
        const performance = window.performance;
        if (!performance.memory) {
            return {};
        }
        const memory = performance.memory;
        return {
            jsHeapSizeLimit: memory.jsHeapSizeLimit,
            totalJSHeapSize: memory.totalJSHeapSize,
            usedJSHeapSize: memory.usedJSHeapSize,
            memoryUsageMB: Math.round(memory.usedJSHeapSize / 1048576 * 100) / 100
        };
    }
    getNetworkInfo() {
        const nav = navigator;
        if (!nav.connection && !nav.mozConnection && !nav.webkitConnection) {
            return {};
        }
        const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
        return {
            effectiveType: connection.effectiveType,
            downlink: connection.downlink,
            rtt: connection.rtt,
            saveData: connection.saveData,
            connectionType: connection.type
        };
    }
    getStorageInfo() {
        const info = {
            localStorage: {
                enabled: false,
                itemCount: 0
            },
            sessionStorage: {
                enabled: false,
                itemCount: 0
            },
            cookies: {
                enabled: navigator.cookieEnabled,
                count: 0
            },
            indexedDB: {
                supported: !!window.indexedDB
            }
        };
        // Check localStorage
        try {
            if (window.localStorage) {
                info.localStorage.enabled = true;
                info.localStorage.itemCount = localStorage.length;
                // Approximate size
                let totalSize = 0;
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key) {
                        const value = localStorage.getItem(key) || '';
                        totalSize += key.length + value.length;
                    }
                }
                info.localStorage.approximateSize = totalSize;
            }
        }
        catch (e) {
            // localStorage not available
        }
        // Check sessionStorage
        try {
            if (window.sessionStorage) {
                info.sessionStorage.enabled = true;
                info.sessionStorage.itemCount = sessionStorage.length;
                // Approximate size
                let totalSize = 0;
                for (let i = 0; i < sessionStorage.length; i++) {
                    const key = sessionStorage.key(i);
                    if (key) {
                        const value = sessionStorage.getItem(key) || '';
                        totalSize += key.length + value.length;
                    }
                }
                info.sessionStorage.approximateSize = totalSize;
            }
        }
        catch (e) {
            // sessionStorage not available
        }
        // Count cookies
        if (document.cookie) {
            info.cookies.count = document.cookie.split(';').filter(c => c.trim()).length;
        }
        return info;
    }
    async getMediaDevices() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
            return null;
        }
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            return {
                hasCamera: devices.some(d => d.kind === 'videoinput'),
                hasMicrophone: devices.some(d => d.kind === 'audioinput'),
                hasSpeakers: devices.some(d => d.kind === 'audiooutput'),
                deviceCount: devices.length
            };
        }
        catch (e) {
            return null;
        }
    }
    getBrowserCapabilities() {
        return {
            webGL: this.checkWebGL(),
            webGL2: this.checkWebGL2(),
            webRTC: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
            serviceWorker: 'serviceWorker' in navigator,
            webAssembly: typeof WebAssembly !== 'undefined',
            webWorkers: typeof Worker !== 'undefined',
            geolocation: 'geolocation' in navigator,
            notifications: 'Notification' in window,
            vibration: 'vibrate' in navigator,
            bluetooth: 'bluetooth' in navigator,
            usb: 'usb' in navigator
        };
    }
    checkWebGL() {
        try {
            const canvas = document.createElement('canvas');
            return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
        }
        catch (e) {
            return false;
        }
    }
    checkWebGL2() {
        try {
            const canvas = document.createElement('canvas');
            return !!canvas.getContext('webgl2');
        }
        catch (e) {
            return false;
        }
    }
    getPageInfo() {
        return {
            title: document.title,
            url: window.location.href,
            referrer: document.referrer,
            documentMode: document.documentMode,
            readyState: document.readyState,
            characterSet: document.characterSet,
            contentType: document.contentType,
            lastModified: document.lastModified,
            domain: document.domain,
            protocol: window.location.protocol
        };
    }
    getResourceInfo() {
        return {
            scripts: document.scripts.length,
            stylesheets: document.styleSheets.length,
            images: document.images.length,
            iframes: document.querySelectorAll('iframe').length,
            videos: document.querySelectorAll('video').length,
            audios: document.querySelectorAll('audio').length,
            links: document.links.length
        };
    }
    getViewportSize() {
        return {
            width: window.innerWidth,
            height: window.innerHeight,
            devicePixelRatio: window.devicePixelRatio,
            orientation: window.screen.orientation?.type ||
                (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait')
        };
    }
    getScrollInfo() {
        const maxX = document.documentElement.scrollWidth - window.innerWidth;
        const maxY = document.documentElement.scrollHeight - window.innerHeight;
        return {
            x: window.scrollX || window.pageXOffset,
            y: window.scrollY || window.pageYOffset,
            maxX: Math.max(0, maxX),
            maxY: Math.max(0, maxY)
        };
    }
    getEmptyData() {
        return {
            performance: {},
            memory: {},
            network: {},
            storage: {
                localStorage: { enabled: false, itemCount: 0 },
                sessionStorage: { enabled: false, itemCount: 0 },
                cookies: { enabled: false, count: 0 },
                indexedDB: { supported: false }
            },
            mediaDevices: null,
            capabilities: {
                webGL: false,
                webGL2: false,
                webRTC: false,
                serviceWorker: false,
                webAssembly: false,
                webWorkers: false,
                geolocation: false,
                notifications: false,
                vibration: false,
                bluetooth: false,
                usb: false
            },
            pageInfo: {
                title: '',
                url: '',
                referrer: '',
                readyState: '',
                characterSet: '',
                contentType: '',
                lastModified: '',
                domain: '',
                protocol: ''
            },
            resources: {
                scripts: 0,
                stylesheets: 0,
                images: 0,
                iframes: 0,
                videos: 0,
                audios: 0,
                links: 0
            },
            viewportSize: {
                width: 0,
                height: 0,
                devicePixelRatio: 1,
                orientation: 'portrait'
            },
            scroll: {
                x: 0,
                y: 0,
                maxX: 0,
                maxY: 0
            },
            timestamp: new Date().toISOString()
        };
    }
}
export default BrowserDebugCollector;
//# sourceMappingURL=browser-debug-info.js.map